import { useRef, useCallback } from "react";

/**
 * Returns the focus to the trigger element after a modal is closed (WCAG 2.1 AA).
 *
 * Tracks a set of trigger elements by ID and records which one opened the most
 * recent modal. Call `restoreFocus()` after closing the modal to refocus that
 * element on the next tick (so the modal has time to unmount).
 *
 * Usage:
 *   const { setTriggerRef, recordTrigger, restoreFocus } =
 *     useFocusReturn<HTMLTableRowElement>();
 *
 *   <tr ref={setTriggerRef(id)} onClick={() => { recordTrigger(id); openModal(); }} />
 *
 *   const onClose = () => { closeModal(); restoreFocus(); };
 */
export function useFocusReturn<T extends HTMLElement>() {
  const triggerRefs = useRef<Map<string, T | null>>(new Map());
  const lastTriggerIdRef = useRef<string | null>(null);

  const setTriggerRef = useCallback(
    (id: string) => (el: T | null) => {
      triggerRefs.current.set(id, el);
    },
    []
  );

  const recordTrigger = useCallback((id: string) => {
    lastTriggerIdRef.current = id;
  }, []);

  const restoreFocus = useCallback(() => {
    const id = lastTriggerIdRef.current;
    if (id) {
      setTimeout(() => triggerRefs.current.get(id)?.focus(), 0);
    }
  }, []);

  /**
   * Returns the trigger element for a given id (used when one component needs
   * to programmatically refocus a specific trigger, e.g. detail-modal → migration-modal
   * fall-through flow).
   */
  const getTriggerRef = useCallback((id: string): T | null => {
    return triggerRefs.current.get(id) ?? null;
  }, []);

  return { setTriggerRef, recordTrigger, restoreFocus, getTriggerRef };
}
